import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Info, Clock, Shirt, MapPin, Car, Plus, Trash2, Save } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";


const DRESS_CODE_OPTIONS = [
  { value: "casual", label: "Casual" },
  { value: "esporte_fino", label: "Esporte Fino" },
  { value: "passeio_completo", label: "Passeio Completo" },
  { value: "traje_social", label: "Traje Social" },
  { value: "black_tie", label: "Black Tie" },
  { value: "white_tie", label: "White Tie" },
  { value: "tematico", label: "Temático" },
  { value: "outro", label: "Outro" }
];

export default function EventInfoEditor() {
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    arrival_time: "",
    ceremony_time: "",
    party_time: "",
    dress_code: "",
    dress_code_custom: "",
    address: "",
    address_link: "",
    parking_info: "",
    additional_info: "",
    custom_fields: []
  });

  useEffect(() => {
    const fetchSite = async () => {
      try {
        const res = await fetch(`${API}/sites/my`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data) {
            setSite(data);
            // Load existing event info
            if (data.event_info) {
              setFormData({
                arrival_time: data.event_info.arrival_time || "",
                ceremony_time: data.event_info.ceremony_time || "",
                party_time: data.event_info.party_time || "",
                dress_code: data.event_info.dress_code || "",
                dress_code_custom: data.event_info.dress_code_custom || "",
                address: data.event_info.address || "",
                address_link: data.event_info.address_link || "",
                parking_info: data.event_info.parking_info || "",
                additional_info: data.event_info.additional_info || "",
                custom_fields: data.event_info.custom_fields || []
              });
            }
          } else {
            navigate('/onboarding');
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSite();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addCustomField = () => {
    setFormData(prev => ({
      ...prev,
      custom_fields: [...prev.custom_fields, { label: "", value: "" }]
    }));
  };

  const updateCustomField = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.custom_fields];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, custom_fields: updated };
    });
  };

  const removeCustomField = (index) => {
    setFormData(prev => ({
      ...prev,
      custom_fields: prev.custom_fields.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const eventInfo = {
        ...formData,
        // If dress_code is "outro", use custom value
        dress_code: formData.dress_code === "outro" ? formData.dress_code_custom : formData.dress_code
      };

      const res = await fetch(`${API}/sites/${site.site_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ event_info: eventInfo })
      });

      if (res.ok) {
        toast.success("Informações salvas com sucesso!");
      } else {
        throw new Error('Erro ao salvar');
      }
    } catch (error) {
      toast.error("Erro ao salvar informações");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-16 h-16 border-4 border-[#F3C9CF] border-t-[#B3121B] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="event-info-editor">
      {/* Header */}
      <header className="bg-white border-b border-[#F3C9CF]/30 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-[#6E0F1A]"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Info className="w-6 h-6 text-[#B3121B]" />
              <span className="font-serif text-xl text-[#6E0F1A]">Informações do Evento</span>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full"
            data-testid="save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Horários */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-[#6E0F1A] flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#B3121B]" />
              Horários
            </CardTitle>
            <CardDescription>Informe os horários importantes do evento</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="arrival_time" className="text-[#6E0F1A]">Horário de Chegada</Label>
                <Input
                  id="arrival_time"
                  name="arrival_time"
                  type="time"
                  value={formData.arrival_time}
                  onChange={handleChange}
                  className="input-wedding mt-2"
                />
              </div>
              <div>
                <Label htmlFor="ceremony_time" className="text-[#6E0F1A]">Horário da Cerimônia</Label>
                <Input
                  id="ceremony_time"
                  name="ceremony_time"
                  type="time"
                  value={formData.ceremony_time}
                  onChange={handleChange}
                  className="input-wedding mt-2"
                />
              </div>
              <div>
                <Label htmlFor="party_time" className="text-[#6E0F1A]">Horário da Festa</Label>
                <Input
                  id="party_time"
                  name="party_time"
                  type="time"
                  value={formData.party_time}
                  onChange={handleChange}
                  className="input-wedding mt-2"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Traje */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-[#6E0F1A] flex items-center gap-2">
              <Shirt className="w-5 h-5 text-[#B3121B]" />
              Traje
            </CardTitle>
            <CardDescription>Qual o código de vestimenta do evento?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="dress_code" className="text-[#6E0F1A]">Código de Vestimenta</Label>
              <Select
                value={formData.dress_code}
                onValueChange={(value) => handleSelectChange("dress_code", value)}
              >
                <SelectTrigger className="input-wedding mt-2">
                  <SelectValue placeholder="Selecione o traje" />
                </SelectTrigger>
                <SelectContent>
                  {DRESS_CODE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.dress_code === "outro" && (
              <div>
                <Label htmlFor="dress_code_custom" className="text-[#6E0F1A]">Especifique o traje</Label>
                <Input
                  id="dress_code_custom"
                  name="dress_code_custom"
                  value={formData.dress_code_custom}
                  onChange={handleChange}
                  placeholder="Ex: Traje country, All white, etc."
                  className="input-wedding mt-2"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Local */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-[#6E0F1A] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#B3121B]" />
              Local
            </CardTitle>
            <CardDescription>Informações sobre o local do evento</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="address" className="text-[#6E0F1A]">Endereço Completo</Label>
              <Textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Rua, número, bairro, cidade..."
                className="input-wedding mt-2"
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="address_link" className="text-[#6E0F1A]">Link do Google Maps</Label>
              <Input
                id="address_link"
                name="address_link"
                value={formData.address_link}
                onChange={handleChange}
                placeholder="https://maps.google.com/..."
                className="input-wedding mt-2"
              />
            </div>
            <div>
              <Label htmlFor="parking_info" className="text-[#6E0F1A] flex items-center gap-2">
                <Car className="w-4 h-4" />
                Informações de Estacionamento
              </Label>
              <Textarea
                id="parking_info"
                name="parking_info"
                value={formData.parking_info}
                onChange={handleChange}
                placeholder="Ex: Estacionamento gratuito no local, valet disponível..."
                className="input-wedding mt-2"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Informações Adicionais */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-[#6E0F1A] flex items-center gap-2">
              <Info className="w-5 h-5 text-[#B3121B]" />
              Informações Adicionais
            </CardTitle>
            <CardDescription>Outras informações importantes para os convidados</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="additional_info" className="text-[#6E0F1A]">Observações Gerais</Label>
              <Textarea
                id="additional_info"
                name="additional_info"
                value={formData.additional_info}
                onChange={handleChange}
                placeholder="Ex: Não é permitida a entrada de crianças, Haverá transporte do hotel para o local..."
                className="input-wedding mt-2"
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        {/* Campos Personalizados */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-[#6E0F1A] flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#B3121B]" />
              Campos Personalizados
            </CardTitle>
            <CardDescription>Adicione informações específicas do seu evento</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.custom_fields.map((field, index) => (
              <div key={index} className="flex gap-4 items-start">
                <div className="flex-1">
                  <Input
                    placeholder="Título (ex: WiFi)"
                    value={field.label}
                    onChange={(e) => updateCustomField(index, "label", e.target.value)}
                    className="input-wedding mb-2"
                  />
                  <Input
                    placeholder="Informação (ex: Rede: Festa123, Senha: casamento)"
                    value={field.value}
                    onChange={(e) => updateCustomField(index, "value", e.target.value)}
                    className="input-wedding"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeCustomField(index)}
                  className="text-red-500 hover:bg-red-50 mt-1"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={addCustomField}
              className="w-full border-dashed border-[#B3121B] text-[#B3121B]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Campo
            </Button>
          </CardContent>
        </Card>

        {/* Save Button Mobile */}
        <div className="md:hidden">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full py-6"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? "Salvando..." : "Salvar Informações"}
          </Button>
        </div>
      </main>
    </div>
  );
}
